import { useState } from 'react';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { CreditCard, AlertTriangle, CheckCircle, FileText, Calendar } from 'lucide-react';
import moment from 'moment';

const VatPaymentVerificationModal = ({ showModal, setShowModal, totalVat, verifying, error, transactions, totalDebit, totalCredit, handleConfirmVerification }) => {
    const [paymentDate, setPaymentDate] = useState(moment().format('YYYY-MM-DD'));

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
    };

    const handleConfirm = () => {
        handleConfirmVerification(paymentDate);
    };

    // Check if verification is balanced
    const isBalanced = Math.abs(totalDebit - totalCredit) <= 0.01;

    return (
        <Modal show={showModal} onHide={() => setShowModal(false)} size="md" centered>
            <Modal.Header closeButton className="border-0 pb-2">
                <Modal.Title className="h5 fw-medium">
                    <div className="d-flex align-items-center">
                        <CreditCard className="me-2" size={20} style={{ color: '#f59e0b' }} />
                        <div>
                            <div className="fw-medium">Betalningsverifikat</div>
                            <div className="small text-muted fw-normal">
                                Momsbetalning till Skatteverket
                            </div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                <style>{`
                    .verification-section {
                        background: #f8fafc;
                        border-radius: 8px;
                        padding: 1rem;
                        margin-bottom: 1rem;
                        border: 1px solid #e2e8f0;
                    }

                    .section-icon {
                        width: 16px;
                        height: 16px;
                        color: #64748b;
                    }

                    .balance-indicator {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.25rem;
                        padding: 0.25rem 0.75rem;
                        border-radius: 6px;
                        font-weight: 500;
                        font-size: 0.75rem;
                    }

                    .balance-good {
                        background: #dcfce7;
                        color: #166534;
                        border: 1px solid #bbf7d0;
                    }

                    .balance-bad {
                        background: #fef2f2;
                        color: #dc2626;
                        border: 1px solid #fecaca;
                    }

                    .compact-table {
                        background: white;
                        border-radius: 6px;
                        overflow: hidden;
                        border: 1px solid #e2e8f0;
                    }

                    .compact-table-header {
                        background: #f8fafc;
                        border-bottom: 1px solid #e2e8f0;
                        padding: 0.5rem 0.75rem;
                        font-size: 0.75rem;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.025em;
                        color: #64748b;
                    }

                    .compact-table-row {
                        padding: 0.5rem 0.75rem;
                        border-bottom: 1px solid #f1f5f9;
                        font-size: 0.875rem;
                        transition: background-color 0.15s ease;
                    }

                    .compact-table-row:hover {
                        background: #f8fafc;
                    }

                    .compact-table-row:last-child {
                        border-bottom: none;
                    }

                    .compact-table-footer {
                        background: #f1f5f9;
                        padding: 0.5rem 0.75rem;
                        font-weight: 600;
                        font-size: 0.875rem;
                        border-top: 1px solid #e2e8f0;
                    }

                    .amount-cell {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                        font-size: 0.8rem;
                    }

                    .date-input-wrapper {
                        position: relative;
                    }

                    .date-input-wrapper input[type="date"] {
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        padding: 0.5rem 0.75rem;
                        font-size: 0.875rem;
                        width: 100%;
                    }

                    .date-input-wrapper input[type="date"]:focus {
                        outline: none;
                        border-color: #3b82f6;
                        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    }
                `}</style>

                {/* Header with balance status */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h6 className="fw-medium mb-1">
                            Momsbetalning: {formatCurrency(totalVat)}
                        </h6>
                        <div className="small text-muted">
                            Betalning till Skatteverket
                        </div>
                    </div>
                    <div className={`balance-indicator ${isBalanced ? 'balance-good' : 'balance-bad'}`}>
                        {isBalanced ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                        {isBalanced ? 'Balanserad' : 'Obalanserad'}
                    </div>
                </div>

                {/* Payment Date Section */}
                <div className="verification-section">
                    <div className="d-flex align-items-center mb-2">
                        <Calendar className="section-icon me-2" />
                        <h6 className="mb-0 fw-medium">Betalningsdatum</h6>
                    </div>
                    <div className="date-input-wrapper">
                        <Form.Control
                            type="date"
                            value={paymentDate}
                            max={moment().format('YYYY-MM-DD')}
                            onChange={(e) => setPaymentDate(e.target.value)}
                        />
                    </div>
                    <div className="small text-muted mt-2">
                        Välj datum när betalningen gjordes.
                    </div>
                </div>

                {/* Transactions section */}
                <div className="verification-section">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                            <FileText className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Transaktioner</h6>
                        </div>
                        <div className="small text-muted">
                            {transactions.length} st
                        </div>
                    </div>

                    {verifying && (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 small">Skapar verifikat...</p>
                        </div>
                    )}

                    {error && (
                        <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.8rem' }}>
                            <AlertTriangle size={14} className="me-2" />
                            {error}
                        </Alert>
                    )}

                    <div className="compact-table">
                        <div className="compact-table-header d-flex">
                            <div style={{ flex: '3' }}>Konto</div>
                            <div style={{ flex: '1', textAlign: 'right' }}>Debet</div>
                            <div style={{ flex: '1', textAlign: 'right' }}>Kredit</div>
                        </div>

                        {transactions.map((transaction, index) => (
                            <div key={index} className="compact-table-row d-flex align-items-center">
                                <div style={{ flex: '3' }}>
                                    <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
                                        {transaction.account_number} - {transaction.account_name}
                                    </div>
                                    <div className="small text-muted">
                                        {transaction.transaction_description}
                                    </div>
                                </div>
                                <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                                    {transaction.debit ? (
                                        <span className="text-success fw-medium">{formatCurrency(transaction.debit)}</span>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </div>
                                <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                                    {transaction.credit ? (
                                        <span className="text-primary fw-medium">{formatCurrency(transaction.credit)}</span>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </div>
                            </div>
                        ))}

                        <div className="compact-table-footer d-flex">
                            <div style={{ flex: '3' }}>Summa</div>
                            <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success">
                                {formatCurrency(totalDebit)}
                            </div>
                            <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary">
                                {formatCurrency(totalCredit)}
                            </div>
                        </div>
                    </div>

                    {!isBalanced && (
                        <div className="alert alert-warning mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                            <AlertTriangle size={14} className="me-2" />
                            <div>
                                <strong>Varning:</strong> Transaktioner är inte balanserade.
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={verifying || !isBalanced}
                >
                    {verifying ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Skapar...
                        </>
                    ) : (
                        'Skapa verifikat'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default VatPaymentVerificationModal;